import React, { useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "../../../../../utils/axios";
import dayjs from "dayjs";

const DeviceChart = ({ id, isDealer = false }) => {
  const [chartData, setChartData] = useState([]);

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const [startTime, setStartTime] = useState(dayjs(yesterday));
  const [endTime, setEndTime] = useState(dayjs(today));

  const fetchData = async () => {
    const modifiedStartTime = startTime.format();
    const modifiedEndTime = endTime.format();
    try {
      const endpoint = isDealer
        ? "dealer/level/report/devices/betweenDates/group/hour"
        : "admin/level/report/devices/betweenDates/group/hour";
      const response = await axios.post(endpoint, {
        deviceId: id,
        start_date: modifiedStartTime,
        end_date: modifiedEndTime,
      });

      // Both Dealer and Admin APIs return data in response.data[0].data.results format
      const data =
        response.data[0]?.data?.results || response.data[0]?.data || [];
      setChartData(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Calculate max value for Y-axis domain
  const getYAxisDomain = () => {
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0)
      return [0, "auto"];

    const values = chartData.map((item) => parseFloat(item.avg_level) || 0);
    const maxValue = Math.max(...values);

    // Ensure maxValue is a valid number
    if (!isFinite(maxValue) || maxValue === 0) return [0, "auto"];

    const domainMax = Math.ceil(maxValue * 1.1 * 100) / 100; // 10% more, rounded to 2 decimals

    return [0, domainMax];
  };

  // Custom tooltip to show rounded values
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = parseFloat(payload[0].value);
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0 }}>
            Level: <strong>{value.toFixed(2)}</strong> m
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: "100%", height: 400 }}>
      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={(data, index) => {
              // Dealer API uses 'date_column', Admin API uses 'date'
              const dateStr = data.date_column || data.date || "";
              if (!dateStr) return "";

              // Extract date and time parts
              // Format: "16-10-2025 16:00:00" or "2025-10-16 16:00:00"
              const parts = dateStr.split(" ");
              const datePart = parts[0];
              const timePart = parts[1] ? parts[1].substr(0, 5) : ""; // Get HH:MM

              // Always show date on first data point
              if (index === 0) {
                return `${datePart}\n${timePart}`;
              }

              // Check if date changed from previous data point
              if (Array.isArray(chartData) && chartData[index - 1]) {
                const prevDateStr =
                  chartData[index - 1]?.date_column ||
                  chartData[index - 1]?.date ||
                  "";
                if (prevDateStr) {
                  const prevDatePart = prevDateStr.split(" ")[0];
                  if (prevDatePart !== datePart) {
                    // Date changed, show both date and time
                    return `${datePart}\n${timePart}`;
                  }
                }
              }

              // Otherwise, just show time
              return timePart;
            }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            domain={getYAxisDomain()}
            tickFormatter={(value) => parseFloat(value).toFixed(2)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="avg_level"
            stroke="#09c4ff"
            fill="#09c4ff"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DeviceChart;
